/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import React, { useState, useEffect } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';

import {
  HeaderIconLinkRow,
  IconLinkVerticalProps,
  LinkButton,
} from '@backstage/core-components';
import {
  useApi,
  identityApiRef,
  alertApiRef,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  Divider,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import BlockIcon from '@material-ui/icons/Block';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import PublishIcon from '@material-ui/icons/Publish';
import ShareIcon from '@material-ui/icons/Share';
import { Alert } from '@material-ui/lab';

import { tomtomLabApiRef } from '../../api';
import { useEventConfig } from '../../hooks/useEventConfig';
import { useParticipant } from '../../hooks/useParticipant';
import { Member, Idea, Status } from '../../types';
import { useIsAdministrator } from '../../util/administratorMethods';
import {
  fetchIdeaMembers,
  fetchSelfAssessments,
  fetchUser,
} from '../../util/fetchMethods';
import { isLocalDevelopment } from '../../util/isLocalDevelopment';
import {
  useCanJuryScore,
  useIsJuryMember,
} from '../../util/juryAssessmentMethods';
import { getEffectiveUserRef } from '../../util/localDevUser';
import { parseTomtomLabResponse } from '../../util/parseMethods';
import { CardContentFields } from '../CardContentFields/CardContentFields';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { IdeaEngagement } from '../IdeaEngagement/IdeaEngagement';
import { AdminOptions } from './AdminOptions';
import { NonParticipantAlert } from './NonParticipantAlert';

export const MAX_IDEA_MEMBERS = 5;

const useStyles = makeStyles(theme => ({
  wordBreak: {
    wordBreak: 'break-all',
    whiteSpace: 'normal',
    margin: '-0.25rem 0',
  },
  scoreBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRight: '0 !important',
    '& a': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderRadius: '100px',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

type Props = {
  initIdea: Idea;
  handleClose: () => void;
};

type ConfirmationAction =
  | 'readyForJury'
  | 'doneHacking'
  | 'cancelIdea'
  | 'deleteIdea'
  | 'readyForHacking'
  | 'readyForFinals'
  | null;

export const IdeaDetailsDialog = ({ initIdea, handleClose }: Props) => {
  const classes = useStyles();
  const tomtomLabApi = useApi(tomtomLabApiRef);
  const identity = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const { isUserParticipant } = useParticipant();
  const [confirmationAction, setConfirmationAction] =
    useState<ConfirmationAction>(null);
  const [isMember, setIsMember] = useState(false);

  const isJuryMember = useIsJuryMember();
  const isAdministrator = useIsAdministrator();

  const [idea, fetchTomtomLabProject] = useAsyncFn(async () => {
    if (initIdea.id) {
      const response = await tomtomLabApi.getIdeaById(initIdea.id);
      return await parseTomtomLabResponse(response);
    }
    return null;
  });
  const canJuryScore = useCanJuryScore(idea.value);

  const [members, fetchMembers] = useAsyncFn(async () => {
    return fetchIdeaMembers(tomtomLabApi, idea.value ?? initIdea);
  });

  const [selfAssessment, fetchSelfAssessment] = useAsyncFn(async () => {
    return fetchSelfAssessments(tomtomLabApi, idea.value ?? initIdea);
  });

  const [userId, fetchUserId] = useAsyncFn(async () => {
    const actualEntityRef = (await identity.getBackstageIdentity())
      .userEntityRef;
    const entityRef = getEffectiveUserRef(actualEntityRef);
    const user = await fetchUser(catalogApi, entityRef);
    if (user) {
      return isLocalDevelopment()
        ? user.metadata.uid || entityRef
        : user.metadata.uid;
    }
    return isLocalDevelopment() ? entityRef : '';
  });

  const [currentUserRef, fetchCurrentUserRef] = useAsyncFn(async () => {
    const actualEntityRef = (await identity.getBackstageIdentity())
      .userEntityRef;
    return getEffectiveUserRef(actualEntityRef);
  });

  useEffect(() => {
    fetchMembers();
    fetchSelfAssessment();
    fetchTomtomLabProject();
    fetchUserId();
    fetchCurrentUserRef();
  }, [
    fetchMembers,
    fetchSelfAssessment,
    fetchTomtomLabProject,
    fetchUserId,
    fetchCurrentUserRef,
  ]);

  useEffect(() => {
    if (members.value && userId.value) {
      setIsMember(
        members.value.some((member: Member) => {
          if (isLocalDevelopment()) {
            return (
              member.userId === userId.value || member.userRef === userId.value
            );
          }

          return member.userId === userId.value;
        }),
      );
    }
  }, [idea.value, members, identity, userId.value]);

  const currentIdea = idea.value || initIdea;
  const isIdeaCanceled = currentIdea.status === 'canceled';
  const { eventConfigs } = useEventConfig(currentIdea.eventId);
  // Administrators can still fix teams while editing is frozen.
  const isTeamEditingFrozen =
    !isAdministrator &&
    eventConfigs?.find(c => c.configKey === 'editTeamMembers')?.metadata
      .value === false;
  const canManageMembers =
    !isIdeaCanceled &&
    !isTeamEditingFrozen &&
    (isAdministrator ||
      (Boolean(currentUserRef.value) &&
        currentIdea.responsible === currentUserRef.value));
  const canCancelCurrentIdea =
    !isIdeaCanceled &&
    (isAdministrator ||
      (Boolean(currentUserRef.value) &&
        currentIdea.responsible === currentUserRef.value));
  const canDeleteCurrentIdea = isAdministrator && Boolean(currentIdea.id);
  const memberCount = members.value?.length ?? currentIdea.membersCount ?? 0;
  const isIdeaFull = !isMember && memberCount >= MAX_IDEA_MEMBERS;

  const handleMembersClick = async () => {
    if (
      !userId.value ||
      !currentIdea.id ||
      isIdeaCanceled ||
      isTeamEditingFrozen
    ) {
      return;
    }

    if (isIdeaFull) {
      return;
    }

    try {
      if (!isMember) {
        const response: any = await tomtomLabApi.addMember(
          currentIdea.id,
          userId.value,
        );
        if (response?.statusCode === 400) {
          alertApi.post({
            severity: 'error',
            message:
              response?.message ??
              "You're already in another submitted idea. Leave it first to join this one.",
          });
          return;
        }
      } else {
        const response: any = await tomtomLabApi.deleteMember(
          currentIdea.id,
          userId.value,
        );

        if (response?.statusCode === 400) {
          alertApi.post({
            severity: 'error',
            message: response?.message,
          });
        }
      }
      setIsMember(!isMember);
      fetchMembers();
    } catch (error) {
      const ideaIsFull =
        error instanceof ResponseError && error.response.status === 409;

      if (ideaIsFull) {
        fetchMembers();
      }

      alertApi.post({
        severity: 'error',
        message: ideaIsFull
          ? `This idea already has the maximum of ${MAX_IDEA_MEMBERS} participants`
          : `Failed to ${isMember ? 'leave' : 'join'} idea`,
      });
    }
  };

  const handleRemoveMember = async (member: Member) => {
    if (!idea.value?.id || !member.userId) {
      return;
    }

    if (member.userRef === (idea.value?.responsible ?? initIdea.responsible)) {
      alertApi.post({
        severity: 'error',
        message: 'The responsible person cannot be removed from the idea',
      });
      return;
    }

    try {
      await tomtomLabApi.deleteMember(idea.value.id, member.userId);
      if (
        member.userId === userId.value ||
        member.userRef === currentUserRef.value
      ) {
        setIsMember(false);
      }
      await fetchMembers();
      alertApi.post({
        severity: 'success',
        message: 'Member removed successfully',
      });
    } catch {
      alertApi.post({
        severity: 'error',
        message: 'Failed to remove member',
      });
    }
  };

  const links: IconLinkVerticalProps[] = [
    {
      label: (() => {
        if (isIdeaCanceled) return 'Join unavailable';
        if (isTeamEditingFrozen) return 'Teams frozen';
        if (isMember) return 'Leave';
        return isIdeaFull ? 'Idea full' : 'Join';
      })(),
      icon: isMember ? <ExitToAppIcon /> : <PersonAddIcon />,
      disabled:
        !isUserParticipant ||
        isIdeaCanceled ||
        isTeamEditingFrozen ||
        isIdeaFull,
      href: '',
      onClick: async () => {
        handleMembersClick();
      },
    },
  ];

  if (idea.error) {
    return <Alert severity="error">{idea?.error?.message}</Alert>;
  } else if (members.error) {
    return <Alert severity="error">{members?.error?.message}</Alert>;
  }

  const isDoneHacking = () => {
    let message: string = '';
    let isDone: Boolean = true;
    if (idea.value?.solution === '') {
      message = `You must have a solution in place before submitting your idea to the final jury. `;
      isDone = false;
    }
    return { message, isDone };
  };

  const isReadyForHacking = () => {
    let message: string = '';
    let isReady: Boolean = true;
    if (idea.value?.status !== 'readyForJury') {
      message = `You must submit your idea to the jury before submitting it for hacking.`;
      isReady = false;
    }
    return { message, isReady };
  };

  const handlePublish = async (status: Status) => {
    const ideaToPublish = {
      ...idea.value,
      status: status,
    };
    const response = await tomtomLabApi.updateIdea(ideaToPublish);
    if (response.status === 'ok') {
      fetchTomtomLabProject();
      setConfirmationAction(null);
      return true;
    }

    alertApi.post({
      severity: 'error',
      message: response.message ?? 'Unable to update idea status',
    });
    return false;
  };

  const handleDeleteIdea = async () => {
    if (!currentIdea.id) {
      return;
    }

    try {
      await tomtomLabApi.deleteIdea(currentIdea.id);
      alertApi.post({
        severity: 'success',
        message: 'Idea deleted successfully',
      });
      setConfirmationAction(null);
      handleClose();
    } catch (error) {
      alertApi.post({
        severity: 'error',
        message: 'Failed to delete idea',
      });
    }
  };

  const handleShare = async () => {
    try {
      const ideaToBeUsed = idea.value ?? initIdea;
      const eventParam = ideaToBeUsed.eventId
        ? `?event=${ideaToBeUsed.eventId}`
        : '';
      // Build a canonical URL so the correct event filter is applied and the
      // idea dialog opens automatically, even for past-event ideas.
      const basePath = window.location.href.split('ideas')[0];
      const path = `${basePath}ideas/${ideaToBeUsed.id}${eventParam}`;
      await navigator.clipboard.writeText(path);
      alertApi.post({
        severity: 'info',
        message: 'Idea link copied to clipboard',
        display: 'transient',
      });
    } catch {
      alertApi.post({
        severity: 'error',
        message: 'There was a problem copying the idea link',
        display: 'transient',
      });
    }
  };

  const handleSubmitToJury = () => {
    setConfirmationAction('readyForJury');
  };

  const handleReadyForHacking = () => {
    const { message, isReady } = isReadyForHacking();
    if (!isReady) {
      alertApi.post({
        severity: 'error',
        message: message,
      });
      return;
    }
    setConfirmationAction('readyForHacking');
  };

  const handleIsDoneHacking = () => {
    const { message, isDone } = isDoneHacking();
    if (!isDone) {
      alertApi.post({
        severity: 'error',
        message: message,
      });
      return;
    }
    setConfirmationAction('doneHacking');
  };

  const ShareButton = () => (
    <Button
      onClick={async () => await handleShare()}
      startIcon={<ShareIcon />}
    />
  );

  const ScoresButton = () => (
    <LinkButton to={`/tomtom-lab/jury-scores/${idea.value?.id}`}>
      SCORES
    </LinkButton>
  );

  const confirmationDialogConfig: Record<
    Exclude<ConfirmationAction, null>,
    {
      title: string;
      message: string[];
      handleSubmit: () => Promise<void>;
    }
  > = {
    readyForJury: {
      title: 'Submit for Jury Evaluation',
      message: [
        `Once you move your idea to "Ready for Jury", you cannot edit the description anymore; are you ready?`,
      ],
      handleSubmit: async () => {
        await handlePublish('readyForJury');
      },
    },
    readyForHacking: {
      title: 'Start Hacking',
      message: [`Are you ready to start hacking?`],
      handleSubmit: async () => {
        await handlePublish('readyForHacking');
      },
    },
    doneHacking: {
      title: 'Submit for Final Jury Evaluation',
      message: [
        'Are you sure you want to mark your idea as Done? Once an Idea is Done Hacking it can no longer be edited.',
      ],
      handleSubmit: async () => {
        await handlePublish('doneHacking');
      },
    },
    readyForFinals: {
      title: 'Submit for Final Jury Evaluation',
      message: [
        'Are you sure you want to mark your idea as Ready for Finals? Once an Idea is Ready for Finals it can no longer be edited.',
      ],
      handleSubmit: async () => {
        await handlePublish('readyForFinals');
      },
    },
    cancelIdea: {
      title: 'Cancel Idea',
      message: ['Are you sure you want to cancel this idea?'],
      handleSubmit: async () => {
        await handlePublish('canceled');
      },
    },
    deleteIdea: {
      title: 'Delete Idea',
      message: ['Are you sure you want to delete this idea?'],
      handleSubmit: async () => {
        await handleDeleteIdea();
      },
    },
  };

  const activeConfirmation = confirmationAction
    ? confirmationDialogConfig[confirmationAction]
    : null;

  return (
    <div>
      <Card>
        <CardHeader
          title={
            <Typography
              variant="h3"
              component="h2"
              className={classes.wordBreak}
            >
              {idea.value?.title || initIdea.title}
            </Typography>
          }
          action={
            <ButtonGroup
              variant="text"
              aria-label="outlined primary button group"
            >
              <ShareButton />
              {(isMember || isAdministrator) &&
                !isIdeaCanceled &&
                idea.value?.status === 'draft' && (
                  <>
                    <LinkButton
                      to={`/tomtom-lab/register-idea/${idea.value?.id}`}
                    >
                      <EditIcon />
                    </LinkButton>
                    <Button
                      onClick={handleSubmitToJury}
                      startIcon={<PublishIcon />}
                    >
                      Ready for jury
                    </Button>
                  </>
                )}
              {(isMember || isAdministrator) &&
                !isIdeaCanceled &&
                idea.value?.status === 'readyForJury' && (
                  <Button
                    onClick={handleReadyForHacking}
                    startIcon={<PublishIcon />}
                  >
                    Ready for hacking
                  </Button>
                )}
              {canCancelCurrentIdea && idea.value?.status !== 'canceled' && (
                <Button
                  onClick={() => setConfirmationAction('cancelIdea')}
                  startIcon={<BlockIcon />}
                >
                  Cancel Idea
                </Button>
              )}
              <AdminOptions
                ideaStatus={idea.value?.status}
                handlePublish={handlePublish}
              />
              {(isMember || isAdministrator) &&
                !isIdeaCanceled &&
                idea.value?.status === 'readyForHacking' && (
                  <>
                    <LinkButton
                      to={`/tomtom-lab/register-idea/${idea.value?.id}`}
                    >
                      <EditIcon />
                    </LinkButton>
                    <Button
                      onClick={handleIsDoneHacking}
                      startIcon={<PublishIcon />}
                    >
                      Done Hacking
                    </Button>
                  </>
                )}
              {(isMember || isAdministrator) &&
                idea.value?.status === 'doneHacking' && (
                  <Button disabled startIcon={<PublishIcon />}>
                    Done Hacking
                  </Button>
                )}
              {canDeleteCurrentIdea && (
                <Button
                  onClick={() => setConfirmationAction('deleteIdea')}
                  startIcon={<DeleteOutlineIcon />}
                >
                  Delete Idea
                </Button>
              )}
              {(isMember || isJuryMember) &&
                idea.value?.status === 'readyForJury' && <ScoresButton />}
              {canJuryScore && (
                <div className={classes.scoreBtn}>
                  <LinkButton
                    to={`/tomtom-lab/jury-assessment/${idea.value?.id}`}
                  >
                    Score
                  </LinkButton>
                </div>
              )}
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
              {activeConfirmation && (
                <ConfirmationDialog
                  open
                  handleClose={() => setConfirmationAction(null)}
                  title={activeConfirmation.title}
                  message={activeConfirmation.message}
                  handleSubmit={activeConfirmation.handleSubmit}
                />
              )}
            </ButtonGroup>
          }
          subheader={<HeaderIconLinkRow links={links} />}
        />
        {!isUserParticipant && <NonParticipantAlert />}
        <Divider />
        <CardContentFields
          idea={currentIdea}
          members={members.value || []}
          selfAssessment={selfAssessment.value}
          canJuryScore={canJuryScore}
          canManageMembers={canManageMembers}
          onRemoveMember={handleRemoveMember}
        />
        <Box px={2} pb={2}>
          <IdeaEngagement
            ideaId={currentIdea.id}
            currentUserRef={currentUserRef.value}
          />
        </Box>
      </Card>
    </div>
  );
};
